import type { Executable, LanguageAdapter } from '../../core/types';
import { PYRuntimeGlue } from './shim';
import { python as cmPython } from '@codemirror/lang-python';
import { lintGutter } from '@codemirror/lint';
import { customScrollbar } from '../../editor/themeScrollbar';
import { breakpointGutter } from '../../editor/breakpoints';
import { getPyodide } from './pyodideLoader';
import { pythonLinterExtension } from '../../editor/pythonLinterExtension';

let _getBreakpoints: () => number[] = () => [];
let _blackReady = false;

async function ensureBlackLoaded() {
    try {
        const pyodide = await getPyodide();
        if (_blackReady) return pyodide;
        // micropip est inclus avec Pyodide
        await pyodide.loadPackage('micropip');
        await pyodide.runPythonAsync(`
import micropip
await micropip.install(["black==24.3.0"])
`);
        _blackReady = true;
        return pyodide;
    } catch {
        // en cas d'échec, on continue sans formatter
        _blackReady = false;
        return await getPyodide();
    }
}

// On étend légèrement le type pour exposer un format() optionnel sans casser LanguageAdapter
export const PythonPyodideAdapter: LanguageAdapter & {
    format?: (src: string) => Promise<string>;
} = {
    id: 'python',

    editorExtensions() {
        return [
            cmPython(),
            lintGutter(),
            customScrollbar,
            breakpointGutter(() => _getBreakpoints()),
            pythonLinterExtension(),
        ];
    },

    async compile(code, bridge, controls) {
        _getBreakpoints = () => controls.breakpoints.getAll().map(b => b.line);
        const payload = await PYRuntimeGlue.compile(code, bridge, controls);
        const exec: Executable = { lang: 'python', payload };
        return exec;
    },

    async runStep(_exec, _controls) {
        // Pas de step-by-step pour Python (optionnel à implémenter plus tard)
        return;
    },

    async runAll(exec, controls) {
        exec.payload.executionState.isRunning = true;
        exec.payload.executionState.isPaused = false;
        await PYRuntimeGlue.runAll(exec.payload, controls);
    },

    async stop(exec) {
        try {
            // demande d'arrêt coopératif côté Python (si le code le respecte)
            (window as any).__PY_SHOULD_STOP__ = true;

            exec.payload.executionState.isRunning = false;
            exec.payload.executionState.isPaused = false;
            await PYRuntimeGlue.stop?.(exec.payload);
        } finally {
            (window as any).__IDE_BRIDGE__?.events?.emit?.('ide:stop');
        }
    },

    registerEventSyntax(_code: string) {
        // (si un jour tu veux @on(...) en Python)
        return {};
    },

    // Formatage Python via Black (best effort, fallback: source d'origine)
    async format(src: string) {
        try {
            const pyodide = await ensureBlackLoaded();
            const formatted = await pyodide.runPythonAsync(`
import black
black.format_str(${JSON.stringify(src)}, mode=black.Mode())
`);
            return String(formatted);
        } catch (e) {
            console.warn('[PY-IDE] format failed, fallback to original:', e);
            return src;
        }
    },
};
